import { useEffect, useState } from "react";
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavList from "examples/Sidenav/SidenavList";
import SidenavItem from "examples/Sidenav/SidenavItem";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel, { sidenavName } from "examples/Sidenav/styles/sidenav";
import { sidenavAvatar } from "examples/Sidenav/styles/sidenavAvatar";
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
} from "context";
import { Popover } from "@mui/material"
import { sidenavBottomContainer } from "./styles/sidenav"
import HomeDefaultCenter from "components/HomeDefaultCenter/HomeDefaultCenter";
import RightButton from "assets/images/apollo-english/right-button.svg";
import LogoutIcon from "components/icons/LogoutIcon";
import { useDispatch } from "react-redux";
import { logOut } from "shared/redux/slices/authSlice";
import { useSelector } from "react-redux";
import { selectAuth, setActiveRoleByCode } from "shared/redux/slices/authSlice";
import { getRoleMenu, selectSwitchableRoles } from "menus/registry";
import UploadAvatar from "pages/my-self/components/UploadAvatar";
import { setOpenNotification } from "context";
import { clearAllNotification } from "shared/redux/slices/notificationSlice";
import { clearAllMySelf } from "../../shared/redux/slices/sliceMySelf";
import { api } from "shared/redux/api";
import { useLazyGetCenterListQuery } from "shared/redux/endpoints/admin";
import { Role, RoleLabel, getRoleCode } from "constant/Role";
import { isPathActive, isNavItemActive, pickActiveRoute, findOpenCollapseKey, findOpenNestedKey } from "procurement/design/nav/matchRoute"

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [openCollapse, setOpenCollapse] = useState(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState(false);
  const [openUploadAvatar, setOpenUploadAvatar] = useState(false);
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } =
    controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const items = pathname.split("/").slice(1);
  const itemParentName = items[1];
  const itemName = items[items.length - 1];

  const handleCloseNotification = () => setOpenNotification(dispatch, false);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "popover-profile" : undefined;

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    const matched = findOpenCollapseKey(pathname, routes)
    setOpenCollapse(matched || collapseName)
    const nested = findOpenNestedKey(pathname, routes)
    setOpenNestedCollapse(nested || itemParentName)
  }, [pathname, routes, collapseName, itemParentName])

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(
        dispatch,
        window.innerWidth < 1200 ? false : transparentSidenav
      );
      // setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
		 */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse) => {
    // Sibling routes can overlap as path prefixes (e.g. "/procurement/orders"
    // vs "/procurement/orders/groups"); only the most specific match among
    // this group of siblings should render as active.
    const bestRoute = pickActiveRoute(
      pathname,
      collapse.map(({ route }) => route)
    );

    const template = collapse.map(({ name, route, key, href, component }) => {
      if (href) {
        return (
          <Link
            key={key}
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem name={name} nested />
          </Link>
        );
      }

      // Nếu có component, sử dụng NavLink
      if (component) {
        return (
          <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
            <SidenavItem name={name} active={route === bestRoute} nested />
          </NavLink>
        );
      }

      // Nếu không có component, dùng navigate
      return (
        <SidenavItem
          key={key}
          name={name}
          active={route === bestRoute}
          nested
          onClick={() => {
            // navigate(route);
          }}
        />
      );
    });

    return template;
  };
  // Render the all the collpases from the routes.js
  const renderCollapse = (collapses) => {
    // Same sibling-prefix-overlap issue as renderNestedCollapse: among the
    // leaf items in this group (no further nested collapse of their own),
    // only the most specific route match should be active.
    const bestLeafRoute = pickActiveRoute(
      pathname,
      collapses.filter((c) => !c.collapse).map((c) => c.route)
    );

    return collapses.map(({ name, collapse, route, href, key, type }) => {
      let returnValue;
      if (type === "hidden") return null;
      if (collapse) {
        returnValue = (
          <SidenavItem
            key={key}
            color={color}
            name={name}
            active={
              isNavItemActive(pathname, { key, route, collapse })
                ? isPathActive(pathname, route)
                  ? true
                  : "isParent"
                : false
            }
            open={openNestedCollapse === key}
            onClick={({ currentTarget }) =>
              openNestedCollapse === key &&
              currentTarget.classList.contains("MuiListItem-root")
                ? setOpenNestedCollapse(false)
                : setOpenNestedCollapse(key)
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      } else {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem color={color} name={name} active={route === bestLeafRoute || key === itemName} />
          </Link>
        ) : (
          <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
            <SidenavItem color={color} name={name} active={route === bestLeafRoute || key === itemName} />
          </NavLink>
        );
      }
      return <SidenavList key={key}>{returnValue}</SidenavList>;
    });
  };

  const auth = useSelector(selectAuth);

  const roleCode = getRoleCode(auth?.role);

  const RoleLabelShort = {
    [Role.TEACHER]: "Teacher",
    [Role.LX]: "LX OPS",
    [Role.LXO]: "LXO",
    [Role.PROCUREMENT_MANAGER]: "Procurement MNG",
  };

  // Compact menu styling is scoped to the LX and LXO roles only; all other
  // roles keep the original sidenav sizing.
  const isLxFamily = roleCode === Role.LX || roleCode === Role.LXO;
  const compact = isLxFamily;

  const [triggerGetCenterList] = useLazyGetCenterListQuery();

  // Roles this user can switch between (only those backed by the menu registry).
  const switchableRoles = useSelector(selectSwitchableRoles);
  const canSwitchRole = switchableRoles.length > 1;
  const [roleAnchorEl, setRoleAnchorEl] = useState(null);
  const roleMenuOpen = Boolean(roleAnchorEl);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes
    .filter(
      (route) =>
        !(route.hiddenInSidenav || route.hiddenForRoles?.includes(roleCode))
    )
    .map(
      ({
        type,
        name,
        icon,
        iconActive,
        title,
        collapse,
        noCollapse,
        key,
        href,
        route,
      }) => {
        let returnValue;

        if (type === "collapse") {
          if (href) {
            returnValue = (
              <Link
                href={href}
                key={key}
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: "none" }}
              >
                <SidenavCollapse
                  name={name}
                  icon={icon}
                  iconActive={iconActive}
                  active={isNavItemActive(pathname, { key, route, collapse }) || key === collapseName}
                  noCollapse={noCollapse}
                  compact={compact}
                />
              </Link>
            );
          } else if (noCollapse && route) {
            returnValue = (
              <NavLink to={route} key={key}>
                <SidenavCollapse
                  name={name}
                  icon={icon}
                  iconActive={iconActive}
                  noCollapse={noCollapse}
                  active={isNavItemActive(pathname, { key, route, collapse }) || key === collapseName}
                  compact={compact}
                >
                  {collapse ? renderCollapse(collapse) : null}
                </SidenavCollapse>
              </NavLink>
            );
          } else {
            returnValue = (
              <NavLink to={route} key={key}>
                <SidenavCollapse
                  key={key}
                  name={name}
                  icon={icon}
                  iconActive={iconActive}
                  active={isNavItemActive(pathname, { key, route, collapse }) || key === collapseName}
                  open={openCollapse === key}
                  compact={compact}
                  onClick={() =>
                    openCollapse === key
                      ? setOpenCollapse(false)
                      : setOpenCollapse(key)
                  }
                >
                  {collapse ? renderCollapse(collapse) : null}
                </SidenavCollapse>
              </NavLink>
            );
          }
        } else if (type === "title") {
          returnValue = (
            <MDTypography
              key={key}
              color={textColor}
              display="block"
              variant="caption"
              fontWeight="bold"
              textTransform="uppercase"
              pl={3}
              mt={2}
              mb={1}
              ml={1}
            >
              {title}
            </MDTypography>
          );
        } else if (type === "divider") {
          returnValue = (
            <Divider
              key={key}
              light={
                (!darkMode && !whiteSidenav && !transparentSidenav) ||
                (darkMode && !transparentSidenav && whiteSidenav)
              }
              sx={{ marginBottom: 1 }}
            />
          );
        }

        return returnValue;
      }
    );

  const navigate = useNavigate();
  const reduxDispatch = useDispatch();

  const handleLogout = () => {
    handleCloseNotification();
    reduxDispatch(clearAllMySelf());
    reduxDispatch(logOut());
    reduxDispatch(clearAllNotification());
    navigate("/sign-in");
  };

  const handleSwitchRole = (code) => {
    setRoleAnchorEl(null);
    if (code === roleCode) return;
    reduxDispatch(setActiveRoleByCode({ code }));
    reduxDispatch(api.util.resetApiState());
    if (code === Role.LXO || code === Role.LX) {
      triggerGetCenterList();
    }
    reduxDispatch(clearAllMySelf());
    reduxDispatch(clearAllNotification());
    navigate(getRoleMenu(code)?.defaultRoute || "/");
  };

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
      sx={{ display: "flex", flexDirection: "columns" }}
    >
      <MDBox sx={{ flexGrow: 1, flexBais: 0 }}>
        <MDBox p={2} textAlign="center">
          <MDBox
            display={{ xs: "block", xl: "none" }}
            position="absolute"
            top={0}
            right={0}
            p={1}
            onClick={closeSidenav}
            sx={{ cursor: "pointer" }}
          >
            <MDTypography variant="h6" color="secondary">
              <Icon sx={{ fontWeight: "bold" }}>close</Icon>
            </MDTypography>
          </MDBox>
          <MDBox
            sx={{
              flexDirection: "column",
              display: miniSidenav ? "block" : "flex",
              alignItems: miniSidenav ? "unset" : "center",
              cursor: "pointer",
            }}
            aria-describedby={id}
            variant="contained"
            onClick={handleClick}
          >
            {auth?.user?.avatar && (
              <MDBox
                component="img"
                src={auth.user.avatar}
                alt={auth.user.fullName}
                width="2rem"
                borderRadius="50%"
                sx={(theme) => sidenavAvatar(theme, { miniSidenav })}
              />
            )}
            <MDBox
              width={!auth?.user?.avatar && "100%"}
              sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
            >
              <MDTypography
                fontWeight="medium"
                variant="span"
                color={textColor}
                sx={(theme) =>
                  sidenavName(theme, {
                    miniSidenav,
                    isAdmin: auth?.role === Role.ADMIN,
                  })
                }
                data-role={auth.role || ""}
                data-email={auth.user.email || ""}
              >
                {auth.user.fullName}
              </MDTypography>
            </MDBox>
          </MDBox>

          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            sx={{
              top: 0,

              "& .MuiPopover-paper": {
                overflowY: "hidden",
                padding: "0 !important",
              },
            }}
          >
            <MDBox
              sx={{
                width: "375px",
                borderRadius: "15px",
                background: "#fff",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: 0,
                boxShadow: 3,
              }}
            >
              <MDBox sx={{ width: "100%", padding: "24px" }}>
                <MDTypography
                  sx={{
                    color: "#1f2024",
                    fontFamily: "Lexend",
                    fontSize: "20px",
                    fontStyle: "normal",
                    fontWeight: 700,
                    lineHeight: "normal",
                    letterSpacing: "0.2px",
                    marginBottom: "16px",
                  }}
                >
                  Quick profile management
                </MDTypography>
                <MDTypography
                  sx={{
                    color: "#71727a",
                    fontFamily: "Lexend",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "20px",
                    marginBottom: "41px",
                  }}
                >
                  Choose your interests.
                </MDTypography>

                <MDBox
                  sx={{ display: "flex", flexDirection: "column", gap: "8px" }}
                >
                  {["Change your password", "Change your avatar"].map(
                    (item, index) => (
                      <MDBox
                        key={index}
                        sx={{
                          width: "100%",
                          height: "52px",
                          padding: "16px",
                          textAlign: "left",
                          borderRadius: "12px",
                          border: "0.5px solid #c5c6cc",
                          cursor: "pointer",

                          "&:hover": {
                            background: "#eaf2ff",
                            borderColor: "#eaf2ff",
                            backgroundImage: `url(${RightButton})`,
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right 16px center",
                          },
                        }}
                        onClick={() => {
                          if (index === 0) {
                            navigate("/myself#change-password");
                            handleClose();
                          } else if (index === 1) {
                            setOpenUploadAvatar(true);
                            handleClose();
                          }
                        }}
                      >
                        <MDTypography
                          sx={{
                            color: "#1f2024",
                            fontFamily: "Lexend",
                            fontSize: "14px",
                            fontStyle: "normal",
                            fontWeight: 400,
                            lineHeight: "20px",
                          }}
                        >
                          {item}
                        </MDTypography>
                      </MDBox>
                    )
                  )}
                </MDBox>
              </MDBox>
            </MDBox>
          </Popover>
          {isLxFamily && (
            <MDBox sx={{ display: miniSidenav ? "none" : "block" }}>
              <HomeDefaultCenter />
            </MDBox>
          )}
          {canSwitchRole && (
            <MDBox sx={{ mt: 1 }}>
              <MDBox
                sx={{
                  backgroundColor: "#EAF2FF",
                  color: "#2576B9 !important",
                  padding: "4px 10px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: "bold",
                  textAlign: "center",
                  display: miniSidenav ? "none" : "block",
                }}
                onClick={(e) => setRoleAnchorEl(e.currentTarget)}
              >
                {`${RoleLabelShort[roleCode] || auth?.role}`}
              </MDBox>
              <Popover
                open={roleMenuOpen}
                anchorEl={roleAnchorEl}
                onClose={() => setRoleAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                sx={{
                  "& .MuiPopover-paper": {
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    boxShadow: 3,
                  },
                }}
              >
                <MDBox sx={{ minWidth: "160px", padding: "8px" }}>
                  {switchableRoles.map((code) => (
                    <MDBox
                      key={code}
                      onClick={() => handleSwitchRole(code)}
                      sx={{
                        padding: "10px 12px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: 14,
                        fontWeight: code === roleCode ? 700 : 400,
                        color: code === roleCode ? "#2576B9" : "#1f2024",
                        "&:hover": { background: "#eaf2ff" },
                      }}
                    >
                      {RoleLabelShort[code] || RoleLabel[code] || code}
                    </MDBox>
                  ))}
                </MDBox>
              </Popover>
            </MDBox>
          )}
          {!isLxFamily && roleCode !== Role.TEACHER && (
            <MDBox sx={{ display: miniSidenav ? "none" : "block", mt: 1 }}>
              <HomeDefaultCenter />
            </MDBox>
          )}
        </MDBox>
        <List
          sx={({ breakpoints }) => ({
            display: "flex",
            flexDirection: "column",
            gap: compact ? 1.5 : 2,
            [breakpoints.down(1600)]: {
              gap: compact ? 1 : 1,
            },
          })}
        >
          {renderRoutes}
        </List>
      </MDBox>

      <MDBox sx={(theme) => sidenavBottomContainer(theme)}>
        <MDBox
          sx={{
            backgroundColor: "#2576B9",
            color: "#FFFFFF !important",
            padding: "5px 10px",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: 14,
            fontWeight: "bold",
          }}
          onClick={handleLogout}
        >
          LOG OUT
        </MDBox>
      </MDBox>
      <UploadAvatar
        open={openUploadAvatar}
        handleClose={() => setOpenUploadAvatar(false)}
      />
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  avatar: PropTypes.string,
};

export default Sidenav;
